import { useState } from "react";

import { PERMISSION_KEY, TRANSLATE_PREFIX } from "../";
import { getMerchantClientBalanceExport } from "../../../../api/merchant";
import { Ipage, TrueOrFalseString } from "../../../../api/types";
import GeneralBtn from "../../../../components/GeneralBtn";
import { Box, Container } from "../../../../components/MuiGenerals";
import TableTab from "../../../../components/TableTab";
import { useAlerting, usePermission, useTranslation } from "../../../../hooks";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../../utils/helper";
import { DivideLine } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import { createZusInstance } from "../../../../zustand/store";
import { Filter } from "./components/Filter";
import Table from "./components/Table";

export interface ZusParamsFace {
  chain_type: number | undefined;
  chain_id: string | undefined;
  asset_names: string[];
  client_ids: string[];
  date_created_from: string;
  date_modified_from: string;
  date_created_to: string;
  date_modified_to: string;
  exclude_zero: TrueOrFalseString;
  comparison_operator: string;
  balance_comparison: string;
  pageSize: number;
  page: number;
}

const initZusParams = {
  page: 0,
  pageSize: 20,
  exclude_zero: "true",
} as const;

export const useZusParams = createZusInstance<Ipage & Partial<ZusParamsFace>>(
  initZusParams
);

function MerchantTab() {
  const zusParams = useZusParams();
  const { hasPermission } = usePermission();
  const { alerting } = useAlerting();
  const { t, tc } = useTranslation(TRANSLATE_PREFIX);
  const [count, setCount] = useState(0);
  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getMerchantClientBalanceExport(
        { ...zusParams.body, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];

    const res = listMapping("name", rawRes, { t, omitKeys });
    const config = {};
    downloadFiles(`Client Balance Merchant Tab`, res, config);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        paddingTop: "2rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <Container disableGutters maxWidth={false}>
        <Filter />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Box sx={customSx.tableExportBar}>
          {hasPermission(PERMISSION_KEY.TabMerchant.Export) && (
            <GeneralBtn label="export" onClick={onExport} />
          )}
        </Box>
        <TableTab>
          <Table {...{ listMapping, setCount }} />
        </TableTab>
      </Container>
    </Box>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config?: { omitKeys: string[]; t: any }
): any[][] => {
  const { omitKeys = [], t } = config || {};
  const res = array.map((item: any) => {
    const chainInfo = findChainInfo(item.chain_type, item.chain_id);
    const decimals = findDecimalByChainNameAndAsset(
      String(item.chain_type),
      String(item.chain_id),
      String(item.asset_name)
    );
    const mappedResult = [
      ["client_id", t("client_id"), item.client_id],
      ["chain_name", t("chain_name"), chainInfo?.name],
      ["currency", t("currency"), item.asset_name],

      [
        "client_balance",
        t("client_balance"),
        displayAmount(item.balance, decimals),
      ],
      [
        "frozen_balance",
        t("frozen_balance"),
        displayAmount(item.frozen_balance, decimals),
      ],
      ["credit", t("credit"), displayAmount(item.credit, decimals)],
      ["creation_time", t("creation_time"), toDisplayTime(item.created_date)],
      [
        "last_modified_date",
        t("last_modified_date"),
        toDisplayTime(item.last_modified_date),
      ],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};

export default MerchantTab;
